import { useEffect, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Dialog from "@/v2-ui/dialog"
import { errorToast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import { useTranslationLinkMutation } from "@/v2-backoffice-client/translation/duplicates/translation.link.mutation"
import { FEATURE_USAGE } from "@/v2-backoffice-client/translation/featureUsage.generated"
import type {
  DuplicateGroup,
  SupportedLanguage,
} from "@/v2-backoffice-client/translation/translation.types"

export type TranslationDuplicateGroupDialogProps = {
  group: DuplicateGroup | null
  languages: SupportedLanguage[]
  onClose(): void
}

// Resolve a duplicate group: inspect every candidate, pick the master, tick
// the codes to link to it, and link them into one group. Nothing is deleted
// or merged - each feature keeps its own row and texts; the link is a
// grouping on top (common.FeatureLinks) so the UI can collapse them and
// edits can propagate.
function TranslationDuplicateGroupDialog(
  props: TranslationDuplicateGroupDialogProps,
) {
  const { group, languages, onClose } = props

  const [master, setMaster] = useState<string>("")
  const [members, setMembers] = useState<Set<string>>(new Set())
  const [detail, setDetail] = useState<string | null>(null)

  const [link, linkState] = useTranslationLinkMutation()
  errorToast(linkState.error, { title: "Link failed" })

  // Default: first code is master, the rest are marked to link into the group.
  useEffect(() => {
    if (!group) return
    const codes = group.rows.map((row) => row.code)
    setMaster(codes[0] ?? "")
    setMembers(new Set(codes.slice(1)))
    setDetail(null)
  }, [group])

  function chooseMaster(code: string) {
    setMaster(code)
    setMembers((prev) => {
      const next = new Set(prev)
      next.delete(code)
      return next
    })
  }

  function toggleMember(code: string, on: boolean) {
    setMembers((prev) => {
      const next = new Set(prev)
      if (on) next.add(code)
      else next.delete(code)
      return next
    })
  }

  async function onLink() {
    if (!master || members.size === 0) return
    const res = await link({
      variables: { masterCode: master, memberCodes: [...members] },
    })
    const result = res.data?.linkFeatures
    if (result) setDetail(result.Detail)
  }

  return (
    <Dialog
      open={!!group}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <Dialog.Content className="w-[980px] max-w-[96vw] max-h-[88vh] overflow-y-auto">
        <Dialog.Header>
          <Dialog.Title>Resolve duplicate: “{group?.name}”</Dialog.Title>
          <Dialog.Description>
            Pick the master, tick the codes to link to it, then link them into
            one group. Nothing is deleted: each feature keeps its own row and
            texts, and edits can later propagate across the linked group.
          </Dialog.Description>
        </Dialog.Header>

        {group ? (
          <div className="flex flex-col gap-2 py-2">
            {group.rows.map((row) => {
              const isMaster = row.code === master
              const usage = FEATURE_USAGE[row.code] ?? []
              return (
                <div
                  key={row.code}
                  className={cn(
                    "grid grid-cols-[auto_auto_1fr] items-start gap-3 rounded-md border p-2",
                    isMaster ? "border-primary" : "border-border",
                  )}
                >
                  <label
                    className="flex items-center gap-1 pt-0.5 text-xs"
                    title="The canonical feature the group collapses to. Linked with @isMaster=1, which demotes any previous master in the same transaction."
                  >
                    <input
                      type="radio"
                      name="dedupe-master"
                      checked={isMaster}
                      onChange={() => chooseMaster(row.code)}
                    />
                    master
                  </label>
                  <label
                    className="flex items-center gap-1 pt-0.5 text-xs"
                    title="Link this feature into the master's group (common.FeatureLinks). Its own row and translations are kept untouched; only the grouping is added."
                  >
                    <input
                      type="checkbox"
                      checked={members.has(row.code)}
                      disabled={isMaster}
                      onChange={(e) => toggleMember(row.code, e.target.checked)}
                    />
                    link
                  </label>
                  <div className="flex flex-col gap-1 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <span className="break-all font-medium">{row.code}</span>
                      {usage.length ? (
                        <div className="flex shrink-0 flex-wrap justify-end gap-x-3 text-xs text-muted-foreground">
                          <span>Used on:</span>
                          {usage.map((usageLink) => (
                            <a
                              key={`${usageLink.url}:${usageLink.label}`}
                              href={usageLink.url}
                              target="_blank"
                              rel="noreferrer"
                              title={usageLink.files.join("\n")}
                              className="text-primary hover:underline"
                            >
                              {usageLink.label} ↗
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {row.category}
                    </span>
                    <div className="flex flex-col gap-0.5 text-xs">
                      {languages.map((lang) => {
                        const text = row.texts.find(
                          (t) => t.languageCode === lang.code,
                        )
                        return (
                          <span key={lang.code}>
                            <span className="text-muted-foreground">
                              {lang.code}:
                            </span>{" "}
                            {text?.name || "—"}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })}

            {detail ? (
              <p className="text-xs text-muted-foreground">{detail}</p>
            ) : null}
          </div>
        ) : null}

        <Dialog.Footer>
          <Button
            size="sm"
            data-feature-code="UI_BackofficeTranslation_Dedupe_LinkGroup"
            onClick={onLink}
            title="Link the ticked features to the master via api.Upsert_Common_FeatureLink (group code = master code). Non-destructive and reversible; full history via system-versioning."
            disabled={!master || members.size === 0 || linkState.loading}
            isLoading={linkState.loading}
          >
            {linkState.loading
              ? "Linking…"
              : `Link into group (${members.size})`}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default TranslationDuplicateGroupDialog
